import React, { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { TableSortLabel } from '@mui/material';
import { useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableRow as MuiTableRow,
  TableHead,
  Typography
} from '@mui/material';
import { borderBottom, styled } from '@mui/system';
import Input from '../../ui/forms/fields/input';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import PrimaryButton from '../../ui/buttons/primaryButton';

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&:last-child': {
    padding: theme.spacing(1)
  },
  borderBottom: 'none'
}));

const TableRow = styled(MuiTableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:first-of-type': {
    '& th': {
      borderBottom: `2px solid ${theme.palette.divider}`
    }
  }
}));

const AdminUserManager = ({
  handleCloseDialog,
  handleDeleteAdminUser,
  handleOpenDialog,
  handleSaveAdminUser,
  confirmDeleteAdminUser,
  deleteDialogOpen,
  dialogOpen,
  loadingAdminUsers,
  adminUsers,
  currentAdminUser,
  setCurrentAdminUser,
  roles,
  setDeleteDialogOpen
}) => {
  // Validation schema using yup
  const validationSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Enter a valid email').required('Email is required')
  });

  // React Hook Form setup
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: currentAdminUser?.firstName || '',
      lastName: currentAdminUser?.lastName || '',
      email: currentAdminUser?.email || ''
    }
  });

  const [sortConfig, setSortConfig] = useState({ key: 'firstName', direction: 'asc' });

  // Update form values when currentAdminUser changes
  useEffect(() => {
    if (currentAdminUser) {
      setValue('firstName', currentAdminUser.firstName || '');
      setValue('lastName', currentAdminUser.lastName || '');
      setValue('email', currentAdminUser.email || '');
    }
  }, [currentAdminUser, setValue]);

  const onSubmit = (data) => {
    handleSaveAdminUser(data);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedAdminUsers = [...(adminUsers || [])].sort((a, b) => {
    const valueA = a[sortConfig.key]?.toLowerCase?.() || '';
    const valueB = b[sortConfig.key]?.toLowerCase?.() || '';

    if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box>
      {loadingAdminUsers && <LinearProgress sx={{ width: '100%' }} />}

      <TableContainer component={Paper} sx={{ padding: 3, borderRadius: 5 }}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <IconButton color="primary" onClick={() => handleOpenDialog()}>
            <AddCircleOutlineIcon sx={{ fontSize: 40 }} />
          </IconButton>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={sortConfig.key === 'firstName' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'firstName'}
                  direction={sortConfig.key === 'firstName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('firstName')}>
                  First Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={sortConfig.key === 'lastName' ? sortConfig.direction : false}>
                <TableSortLabel
                  active={sortConfig.key === 'lastName'}
                  direction={sortConfig.key === 'lastName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('lastName')}>
                  Last Name
                </TableSortLabel>
              </TableCell>

              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAdminUsers &&
              sortedAdminUsers.length > 0 &&
              sortedAdminUsers.map((adminUser, index) => (
                <TableRow key={index}>
                  <TableCell>{adminUser.firstName}</TableCell>
                  <TableCell>{adminUser.lastName}</TableCell>
                  <TableCell>{adminUser.email}</TableCell>
                  <TableCell>
                    {adminUser.roles && adminUser.roles.length > 0 ? (
                      adminUser.roles
                        .filter((role) => !role.hidden)
                        .map(
                          (role, index, filteredRoles) =>
                            `${role.name}${index < filteredRoles.length - 1 ? ', ' : ''}`
                        )
                        .join('') || (
                        <Typography variant="body2" sx={{ fontSize: '10px', color: 'gray' }}>
                          No Roles Assigned
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '10px', color: 'gray' }}>
                        No Roles Assigned
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenDialog(adminUser)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteAdminUser(adminUser._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}>
            <CloseIcon sx={{ fontSize: 30 }} />
          </IconButton>
          <DialogContent sx={{ mt: 5 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              <DialogActions>
                <Button onClick={handleCloseDialog} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" color="primary">
                  Save
                </Button>
              </DialogActions>
            </form>
          </DialogContent>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth>
        <DialogTitle>Are you sure you want to delete this admin user?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteAdminUser} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserManager;
